/**
 * /join/:slug — public Ox Ambassador-branded sign-up page.
 *
 * Silent linking: the customer never sees "you're linked to X". They just
 * see the Ambassador's branding at the top ("Brought to you by …") and a
 * message that signing up here unlocks best rates. If their email is
 * already registered we transparently switch to an inline sign-in form
 * that links them to the Ambassador on the backend.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatApiError } from '@/utils/apiError';
import { dropReferralCookie } from '@/utils/referralCookie';

export default function AffiliateSignup() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [aff, setAff] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mode toggles between "create new account" and "sign in to existing".
  // We flip automatically when the register endpoint says the email is
  // already taken — the customer never sees the technical error.
  const [mode, setMode] = useState('signup');
  // Brand access — installers pick which product lines they want pricing
  // for on signup. Unticked brands stay greyed out on their dashboard
  // until Ox staff turns them on. At least one must be ticked.
  const BRANDS = [
    { id: 'probound', label: 'Bound', accent: 'text-[#2F5E96] border-[#2F5E96]' },
    { id: 'proepoxy', label: 'Epoxy', accent: 'text-[#7A0B7A] border-[#7A0B7A]' },
    { id: 'prodecor', label: 'Decor', accent: 'text-[#D5AB09] border-[#D5AB09]' },
  ];
  const [access, setAccess] = useState({ probound: false, proepoxy: false, prodecor: false });
  const [form, setForm] = useState({ email: '', password: '', name: '', company_name: '', phone: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/affiliates/${slug}`);
        if (r.ok) {
          const data = await r.json();
          setAff(data);
          // Belt-and-braces attribution — the moment someone lands on
          // the branded page we drop a 30-day cookie. Covers the "click
          // now, sign up later" case that used to lose the referral.
          dropReferralCookie(data.slug || slug);
        }
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const redirectAfterAuth = (role) => {
    if (role === 'affiliate') navigate('/affiliate');
    else navigate('/');
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'signin') {
        // Existing installer flow — silently links them to the Ambassador
        // on the backend, no acknowledgement leaked in the UI.
        const r = await fetch(`${API_URL}/api/public/affiliates/${slug}/link-existing`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email, password: form.password }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(formatApiError(data) || 'Sign in failed');
        toast.success('Welcome back!');
        redirectAfterAuth(data.role);
        return;
      }

      // Signup path — enforce at least one product ticked before hitting
      // the API so we don't waste a round-trip on a validation the backend
      // is going to reject.
      if (!Object.values(access).some(Boolean)) {
        toast.error('Pick at least one product you want pricing for.');
        setBusy(false);
        return;
      }

      const r = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, affiliate_slug: slug, requested_access: access }),
      });
      if (r.ok) {
        toast.success(`Welcome${aff?.name ? ` — best rates unlocked!` : ''}`);
        navigate('/');
        return;
      }
      const errData = await r.json();
      const msg = formatApiError(errData) || 'Sign up failed';
      // Silent switch to the sign-in form when the email is already taken.
      // We keep the entered email + phone so the customer just has to
      // add their password. Nothing tells them "you're being linked".
      if (r.status === 400 && /already registered/i.test(msg)) {
        setMode('signin');
        toast.info('Looks like you already have an account — sign in to unlock best rates.');
        return;
      }
      throw new Error(msg);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">Loading…</div>;

  const isSignin = mode === 'signin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6" data-testid="affiliate-signup-card">
        {aff ? (
          <div className="text-center mb-6 pb-6 border-b">
            {aff.has_logo && (
              <img
                src={`${API_URL}/api/staff/affiliates/${aff.id}/logo`}
                alt={aff.name}
                className="h-16 mx-auto mb-3 object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <p className="text-xs uppercase tracking-wider text-gray-500">Brought to you by</p>
            <h1 className="text-xl font-bold text-gray-900 mt-1" data-testid="affiliate-name">{aff.name}</h1>
            <p className="text-sm text-gray-600 mt-2">
              {isSignin
                ? 'Sign in below to unlock your best rates on every order.'
                : 'Set up an Ox Resin account through this link and get our best rates on every order.'}
            </p>
          </div>
        ) : (
          <div className="text-center mb-6 pb-6 border-b">
            <h1 className="text-xl font-bold text-gray-900">Create your Ox Resin account</h1>
            <p className="text-sm text-amber-700 mt-2">This referral link is no longer active, but you can still sign up below.</p>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          {!isSignin && (
            <>
              <div><Label>Your name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name" /></div>
              <div><Label>Company (optional)</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
              {/* Brand pickers — installer ticks which products they want
                  pricing for. Unticked brands stay greyed out in the app
                  until Ox staff activates them. Backend rejects a signup
                  with none ticked (belt-and-braces to the frontend check
                  in submit()). */}
              <div>
                <Label>Which products do you want prices for?</Label>
                <div className="mt-2 grid grid-cols-3 gap-2" data-testid="signup-brand-picker">
                  {BRANDS.map((b) => {
                    const on = !!access[b.id];
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setAccess((prev) => ({ ...prev, [b.id]: !prev[b.id] }))}
                        data-testid={`signup-brand-${b.id}`}
                        aria-pressed={on}
                        className={[
                          'relative rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-all',
                          on
                            ? `bg-white ${b.accent} shadow-sm`
                            : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200',
                        ].join(' ')}
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          <span
                            aria-hidden
                            className={[
                              'w-4 h-4 rounded flex items-center justify-center text-white text-[10px] font-bold border-2 transition-colors',
                              on ? 'bg-emerald-600 border-emerald-600' : 'bg-transparent border-gray-300',
                            ].join(' ')}
                          >
                            {on && '✓'}
                          </span>
                          {b.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-500 mt-1.5">Pick at least one — unticked products stay hidden until Ox activates them.</p>
              </div>
            </>
          )}
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" /></div>
          <div>
            <Label>Password</Label>
            <Input
              type="password" required
              minLength={isSignin ? undefined : 8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="signup-password"
            />
          </div>
          {!isSignin && (
            <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          )}
          <Button type="submit" disabled={busy} className="w-full bg-[#7A0B7A] hover:bg-[#5e0860] text-white" data-testid="signup-submit">
            {busy ? (isSignin ? 'Signing in…' : 'Creating account…') : (isSignin ? 'Sign in' : 'Create account')}
          </Button>
          <p className="text-xs text-gray-500 text-center pt-2">
            {isSignin ? (
              <>Don't have an account?{' '}
                <button type="button" className="text-[#7A0B7A] font-semibold" onClick={() => setMode('signup')}>
                  Create one
                </button>
              </>
            ) : (
              <>Already have an account?{' '}
                <button type="button" className="text-[#7A0B7A] font-semibold" onClick={() => setMode('signin')} data-testid="switch-to-signin">
                  Sign in
                </button>
              </>
            )}
          </p>
        </form>
      </Card>
    </div>
  );
}
